import { useEffect, useState } from "react";
import { api, fmtEur, COMPANY_COLORS } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";
import { ReportExport } from "@/components/ReportExport";
import { currentCompetenzaMese } from "@/lib/api";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const STATI = [{ v: "tutti", l: "Tutti" }, { v: "debito", l: "In debito" }, { v: "zero", l: "A zero" }, { v: "credito", l: "Saldo positivo" }];
const STATO_BADGE = { debito: ["bg-red-600", "In debito"], zero: ["bg-slate-500", "A zero"], credito: ["bg-green-600", "Saldo positivo"] };

const Cell = ({ v, d, showDiff }) => (
  <td className="text-right font-mono whitespace-nowrap">
    <div className={v > 0.005 ? "text-red-600 dark:text-red-400" : v < -0.005 ? "text-green-700 dark:text-green-400" : "text-muted-foreground"}>{fmtEur(v)}</div>
    {showDiff && Math.abs(d) > 0.005 && <div className={`text-[10px] ${d > 0 ? "text-red-500" : "text-green-600"}`}>{d > 0 ? "+" : ""}{fmtEur(d)}</div>}
  </td>
);

export default function Investimento() {
  const { company } = useCompany();
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [stato, setStato] = useState("tutti");
  const [q, setQ] = useState("");
  const [showDiff, setShowDiff] = useState(true);
  useEffect(() => {
    setData(null);
    api.get("/investimento", { params: { anno, company } }).then((r) => setData(r.data))
      .catch(() => { setData({ righe: [], totali: [], totali_diff: [] }); toast.error("Errore nel caricamento della tabella"); });
  }, [anno, company]);

  const t = q.trim().toLowerCase();
  const righe = (data?.righe || []).filter((r) => (stato === "tutti" || r.stato === stato) && (!t || r.label.toLowerCase().includes(t)));
  const conta = (s) => (data?.righe || []).filter((r) => r.stato === s).length;
  const tot = MESI.map((_, i) => righe.reduce((a, r) => a + r.valori[i], 0));
  const totD = MESI.map((_, i) => righe.reduce((a, r) => a + r.diff[i], 0));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-black">Tabella investimento</h1>
          <div className="text-sm text-muted-foreground">Saldo di fine mese di ogni conteggio: in rosso quanto il padroncino ci deve (investito), in verde se ha saldo positivo.</div>
        </div>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setAnno(anno - 1)} data-testid="inv-prev-anno"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="font-heading font-bold w-14 text-center" data-testid="inv-anno">{anno}</span>
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setAnno(anno + 1)} data-testid="inv-next-anno"><ChevronRight className="w-4 h-4" /></Button>
          <span className="w-2" />
          <ReportExport kind="investimento" params={{ anno, company }} testId="inv-export"
                        completoParams={{ company, anno, mese: anno === new Date().getFullYear() ? currentCompetenzaMese() : `${anno}-12` }} />
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {STATI.map((s) => (
          <Button key={s.v} size="sm" variant={stato === s.v ? "default" : "outline"} className="h-8" onClick={() => setStato(s.v)} data-testid={`inv-stato-${s.v}`}>
            {s.l}{s.v !== "tutti" && data ? ` (${conta(s.v)})` : ""}
          </Button>))}
        <div className="relative w-64">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca padroncino o targa..." className="h-8 pl-7" data-testid="inv-search" />
        </div>
        <Button size="sm" variant="ghost" className="h-8" onClick={() => setShowDiff(!showDiff)} data-testid="inv-toggle-diff">{showDiff ? "Nascondi" : "Mostra"} differenze</Button>
      </div>
      <Card className="rounded-md overflow-x-auto">
        <table className="autotrans-table w-full text-xs" data-testid="inv-table">
          <thead><tr><th className="sticky left-0 bg-card z-10 min-w-[220px]">Conteggio</th>{MESI.map((m) => <th key={m} className="text-right">{m}</th>)}<th>Stato</th></tr></thead>
          <tbody>
            {!data && <tr><td colSpan={14} className="text-center py-6 text-muted-foreground">Caricamento…</td></tr>}
            {data && righe.length === 0 && <tr><td colSpan={14} className="text-center py-6 text-muted-foreground">Nessun conteggio</td></tr>}
            {righe.map((r) => (
              <tr key={r.padroncino_id} data-testid={`inv-row-${r.padroncino_id}`}>
                <td className="sticky left-0 bg-card z-10 font-medium">
                  {company === "all" && <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: COMPANY_COLORS[r.company] }} />}{r.label}
                </td>
                {r.valori.map((v, i) => <Cell key={i} v={v} d={r.diff[i]} showDiff={showDiff} />)}
                <td><Badge className={`${STATO_BADGE[r.stato][0]} text-white h-5 whitespace-nowrap`}>{STATO_BADGE[r.stato][1]}</Badge></td>
              </tr>
            ))}
          </tbody>
          {righe.length > 0 && <tfoot><tr className="font-bold">
            <td className="sticky left-0 bg-card z-10 font-heading">TOTALE INVESTITO</td>
            {tot.map((v, i) => <Cell key={i} v={v} d={totD[i]} showDiff={showDiff} />)}<td data-testid="inv-totale">{fmtEur(tot[new Date().getFullYear() === anno ? new Date().getMonth() : 11])}</td>
          </tr></tfoot>}
        </table>
      </Card>
    </div>
  );
}
